import { Badge } from '@/components/ui/shadcn/badge';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { useTranslations } from '@/hooks/useTranslations';
import type { User } from '@/types';
import { Link, router } from '@inertiajs/react';
import { Briefcase, Building2, Mail, Phone } from 'lucide-react';
import { useCallback, useRef } from 'react';
import { route } from 'ziggy-js';

interface EmployeeCardProps {
    employee: User;
}

export function EmployeeCard({ employee }: EmployeeCardProps) {
    const { t } = useTranslations();
    const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim();

    // Track touch events to distinguish scrolls from taps
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const isScrollingRef = useRef(false);

    // Handle touch start - record initial position
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (touch) {
            touchStartRef.current = { x: touch.clientX, y: touch.clientY };
            isScrollingRef.current = false;
        }
    }, []);

    // Handle touch move - detect if user is scrolling
    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (!touchStartRef.current) return;

        const touch = e.touches[0];
        if (!touch) return;

        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
        const threshold = 10; // pixels - adjust if needed

        // If movement exceeds threshold, user is scrolling
        if (deltaX > threshold || deltaY > threshold) {
            isScrollingRef.current = true;
        }
    }, []);

    // Handle navigation - only navigate if it was a tap, not a scroll
    const handleNavigation = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            const isTouch = 'touches' in e || 'changedTouches' in e;

            if (isTouch || window.innerWidth < 768) {
                // If it was a scroll gesture, don't navigate
                if (isScrollingRef.current) {
                    touchStartRef.current = null;
                    isScrollingRef.current = false;
                    return;
                }

                e.preventDefault();
                e.stopPropagation();
                router.visit(route('employees.show', employee.id), {
                    preserveState: false,
                    preserveScroll: false,
                });
            }

            // Reset refs after handling
            touchStartRef.current = null;
            isScrollingRef.current = false;
        },
        [employee.id],
    );

    // Ensure we have a valid ID
    if (!employee?.id) {
        console.error('Employee ID is missing:', employee);
        return null;
    }

    return (
        <Link
            href={route('employees.show', employee.id)}
            className="touch-target block"
            onClick={handleNavigation}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleNavigation}
        >
            <Card className="mb-2 cursor-pointer transition-shadow hover:shadow-sm">
                <CardContent className="p-3">
                    <div className="flex flex-col gap-2">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <h3 className="inline-flex items-center gap-1.5 text-sm font-semibold">
                                    {fullName}
                                    {employee.azure_id && (
                                        <span
                                            aria-label="Microsoft 365"
                                            title={t('employees.tables.m365_badge', 'Microsoft 365 user')}
                                            className="inline-flex shrink-0"
                                        >
                                            <svg width="12" height="12" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                                                <path fill="#F25022" d="M1 1h10v10H1z" />
                                                <path fill="#7FBA00" d="M12 1h10v10H12z" />
                                                <path fill="#00A4EF" d="M1 12h10v10H1z" />
                                                <path fill="#FFB900" d="M12 12h10v10H12z" />
                                            </svg>
                                        </span>
                                    )}
                                </h3>
                                {employee.position && (
                                    <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-xs">
                                        <Briefcase className="h-3.5 w-3.5 flex-shrink-0" />
                                        <span className="truncate">{employee.position}</span>
                                    </div>
                                )}
                            </div>
                            {employee.role && (
                                <Badge variant="outline" className="flex-shrink-0 px-1.5 py-0 text-xs">
                                    {employee.role}
                                </Badge>
                            )}
                        </div>

                        {/* Details */}
                        <div className="space-y-1">
                            {employee.email && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{employee.email}</span>
                                </div>
                            )}

                            {employee.phone && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{employee.phone}</span>
                                </div>
                            )}

                            {employee.companies && employee.companies.length > 0 && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{employee.companies.map((c) => c.name).join(', ')}</span>
                                </div>
                            )}

                            {employee.department && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <span className="truncate">Dept: {employee.department}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}
